import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MomLayout from './MomLayout';
import { useMomAuth } from '../../context/MomAuthContext';
import { fetchWithAuth } from '../../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Sparkles, Loader2, X, Download,
  Video as VideoIcon, RefreshCw, Image as ImageIcon
} from 'lucide-react';
import { InsufficientCreditsModal } from '../../components/credits/InsufficientCreditsModal';

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => resolve(e.target.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const stripPrefix = (dataUrl) => (dataUrl?.includes('base64,') ? dataUrl.split('base64,')[1] : dataUrl);

export default function MomVideoPage() {
  const navigate = useNavigate();
  const { mom } = useMomAuth();
  const pollRef = useRef(null);

  const [sourceDataUrl, setSourceDataUrl] = useState(null);
  const [seededFrom4K, setSeededFrom4K] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  const [videoUrl, setVideoUrl] = useState(null);
  const [creditsError, setCreditsError] = useState(null);

  useEffect(() => {
    // pre-seed from previous 4K result if available
    try {
      const seed = sessionStorage.getItem('mom_video_seed');
      if (seed) {
        setSourceDataUrl(seed);
        setSeededFrom4K(true);
        sessionStorage.removeItem('mom_video_seed');
      }
    } catch {/* ignore */}
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Görüntü 10MB\'den küçük olmalı');
      return;
    }
    const dataUrl = await fileToBase64(file);
    setSourceDataUrl(dataUrl);
    setSeededFrom4K(false);
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetchWithAuth(`/generation/4d-simulation/status/${jobId}`);
        if (res?.progress) setProgress(res.progress);
        if (res?.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (res?.video_url) {
            setVideoUrl(res.video_url);
            toast.success('Video hazır!');
          } else {
            toast.error('Video oluşturulamadı, lütfen tekrar deneyin.');
          }
          setGenerating(false);
        } else if (res?.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.error(res?.error || 'Video üretimi başarısız oldu');
          setGenerating(false);
        }
      } catch (err) {
        console.warn('Video poll error', err);
      }
    }, 4000);
  };

  const onGenerate = useCallback(async () => {
    if (!sourceDataUrl) {
      toast.error('Lütfen önce bir bebek yüzü görüntüsü yükleyin.');
      return;
    }
    setGenerating(true);
    setVideoUrl(null);
    setProgress('Video isteği gönderiliyor...');
    setCreditsError(null);

    try {
      const startRes = await fetchWithAuth('/generation/4d-simulation/start', {
        method: 'POST',
        body: JSON.stringify({
          image_base64: stripPrefix(sourceDataUrl),
          source_type: seededFrom4K ? 'baby_face' : 'photo',
          animation_style: 'natural',
        }),
      });
      if (startRes?.job_id) {
        toast.success('Video üretimi başlatıldı! Bu birkaç dakika sürebilir.');
        startPolling(startRes.job_id);
      } else {
        setGenerating(false);
        toast.error('Video başlatılamadı.');
      }
    } catch (err) {
      setGenerating(false);
      if (err?.status === 402 && err?.detail) {
        setCreditsError(err.detail);
      } else {
        toast.error(err?.message || 'Bir hata oluştu');
      }
    }
  }, [sourceDataUrl, seededFrom4K]);

  const onDownload = async () => {
    if (!videoUrl) return;
    try {
      const res = await fetch(videoUrl);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `bebek_video_${(mom?.first_name || 'mom')}_${new Date().toISOString().split('T')[0]}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
    } catch (err) {
      toast.error('İndirme başarısız: ' + (err?.message || ''));
    }
  };

  const onReset = () => {
    setVideoUrl(null);
    setSourceDataUrl(null);
    setSeededFrom4K(false);
    setProgress('');
  };

  const canGenerate = !!sourceDataUrl && !generating;

  return (
    <MomLayout>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-5">
        <button
          onClick={() => navigate('/mom/dashboard')}
          className="w-10 h-10 rounded-2xl bg-white border border-[#F3E5E0] flex items-center justify-center text-[#7A6C68] hover:text-[#FB7185]"
          data-testid="mom-video-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="text-center flex-1 px-3">
          <p className="text-xs text-[#7A6C68]">4D Animasyon</p>
          <h1 className="text-base font-bold text-[#2D2422]">Video Animasyon</h1>
        </div>
        <div className="w-10" />
      </div>

      {!videoUrl ? (
        <>
          {/* Hero */}
          <div
            className="rounded-3xl p-5 mb-5 shadow-sm relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)' }}
            data-testid="mom-video-hero"
          >
            <Sparkles className="absolute top-4 right-4 w-5 h-5 text-white/40" />
            <p className="text-white/85 text-xs font-medium mb-1">Bebek yüzünü canlandır</p>
            <h2 className="text-xl font-bold text-white leading-snug mb-2">
              Tek bir görüntüden <br /> kısa bir bebek videosu
            </h2>
            <p className="text-white/90 text-xs leading-relaxed">
              4K bebek yüzü veya Genetik Füzyon sonucu kullanılabilir. AI nazikçe gözleri açar, küçük bir tebessüm ekler.
              Her video <span className="font-semibold">2 kredi</span> tüketir.
            </p>
          </div>

          {/* Image picker */}
          <p className="text-sm font-semibold text-[#2D2422] mb-2">
            Kaynak görüntü <span className="text-[#FB7185]">*</span>
          </p>

          <div
            className={`relative aspect-[4/5] rounded-3xl border-2 border-dashed flex items-center justify-center overflow-hidden transition-all mb-3 ${
              sourceDataUrl ? 'border-transparent bg-white shadow-sm' : 'border-[#F3D8D0] bg-[#FFF6F3]'
            }`}
            data-testid="mom-video-source-tile"
          >
            {sourceDataUrl ? (
              <>
                <img src={sourceDataUrl} alt="Kaynak" className="w-full h-full object-cover" />
                {seededFrom4K && (
                  <span
                    className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-[#FB7185] text-white text-[10px] font-medium"
                    data-testid="mom-video-from-4k-badge"
                  >
                    4K sonucu
                  </span>
                )}
                <button
                  onClick={() => { setSourceDataUrl(null); setSeededFrom4K(false); }}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                  data-testid="mom-video-source-remove"
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <label className="cursor-pointer w-full h-full flex flex-col items-center justify-center text-center px-6 gap-3">
                <div className="w-16 h-16 rounded-2xl bg-[#F3E8FF] text-[#8B5CF6] flex items-center justify-center">
                  <ImageIcon className="w-7 h-7" />
                </div>
                <p className="text-sm font-medium text-[#2D2422]">Bebek yüzünü yükle</p>
                <p className="text-xs text-[#7A6C68] leading-relaxed max-w-[220px]">
                  Önceden ürettiğin bir bebek yüzünü kullanabilirsin.
                </p>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFile}
                  data-testid="mom-video-source-input"
                />
              </label>
            )}
          </div>

          {/* Quick navigation hints */}
          {!sourceDataUrl && (
            <div className="grid grid-cols-2 gap-3 mb-5">
              <button
                onClick={() => navigate('/mom/upload')}
                className="rounded-2xl bg-white border border-[#F3E5E0] py-3 text-xs font-medium text-[#7A6C68] flex items-center justify-center gap-2 active:scale-[0.98]"
                data-testid="mom-video-from-4k-btn"
              >
                <Upload className="w-4 h-4" />
                4K Üret
              </button>
              <button
                onClick={() => navigate('/mom/ai-face')}
                className="rounded-2xl bg-white border border-[#F3E5E0] py-3 text-xs font-medium text-[#7A6C68] flex items-center justify-center gap-2 active:scale-[0.98]"
                data-testid="mom-video-from-aiface-btn"
              >
                <Sparkles className="w-4 h-4" />
                Genetik Füzyon
              </button>
            </div>
          )}

          {/* Generate button */}
          <button
            onClick={onGenerate}
            disabled={!canGenerate}
            className={`w-full h-14 rounded-2xl text-white font-semibold shadow-sm transition-all flex items-center justify-center gap-2 mt-2 ${
              canGenerate
                ? 'bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] hover:shadow-md active:scale-[0.98]'
                : 'bg-[#E5DAD6] cursor-not-allowed'
            }`}
            data-testid="mom-video-generate-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {progress || 'Üretiliyor...'}
              </>
            ) : (
              <>
                <VideoIcon className="w-5 h-5" />
                Video Animasyon Üret · 2 kredi
              </>
            )}
          </button>

          {generating && (
            <p className="text-xs text-[#7A6C68] mt-3 text-center" data-testid="mom-video-progress-hint">
              Bu işlem 2-4 dakika sürebilir. Sayfayı açık tut.
            </p>
          )}
        </>
      ) : (
        /* Result */
        <div className="space-y-4" data-testid="mom-video-result">
          <div className="rounded-3xl bg-black p-2 shadow-sm">
            <video
              src={videoUrl}
              controls
              autoPlay
              loop
              playsInline
              className="w-full aspect-[4/5] object-cover rounded-2xl"
              data-testid="mom-video-result-player"
            />
          </div>
          <p className="text-center text-xs text-[#7A6C68]">
            Bu animasyon AI yorumudur — gerçek bir hareket kaydı değildir.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={onDownload}
              className="rounded-2xl bg-white border border-[#F3E5E0] py-3 text-sm font-medium text-[#2D2422] flex items-center justify-center gap-2 active:scale-[0.98]"
              data-testid="mom-video-download-btn"
            >
              <Download className="w-4 h-4" />
              İndir
            </button>
            <button
              onClick={onReset}
              className="rounded-2xl bg-[#F3E8FF] py-3 text-sm font-medium text-[#8B5CF6] flex items-center justify-center gap-2 active:scale-[0.98]"
              data-testid="mom-video-reset-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Yenisini Üret
            </button>
          </div>
        </div>
      )}

      <InsufficientCreditsModal
        open={!!creditsError}
        onClose={() => setCreditsError(null)}
        detail={creditsError}
      />
    </MomLayout>
  );
}
